const N = 624;
const M = 397;
const MATRIX_A = 0x9908b0df;
const UPPER_MASK = 0x80000000;
const LOWER_MASK = 0x7fffffff;

function mulUint32(a: number, b: number): number {
  return Math.imul(a, b) >>> 0;
}

function seedToKeyArray(seed: number): number[] {
  let n = Math.abs(Math.trunc(seed));
  if (n === 0) return [0];
  const words: number[] = [];
  while (n > 0) {
    words.push(n % 0x100000000);
    n = Math.floor(n / 0x100000000);
  }
  return words;
}

/**
 * Bit-for-bit port of CPython's `random.Random(seed)` (MT19937, seeded via
 * `init_by_array` exactly as CPython's random_seed does for an int seed) so
 * `.random()` produces the same draw sequence as the backend's PRNG for a
 * given seed. Only int seeds (as used for avatar seeds) are supported.
 */
class MT19937 {
  private mt = new Uint32Array(N);
  private mti = N + 1;

  constructor(seed: number) {
    this.initByArray(seedToKeyArray(seed));
  }

  private initGenrand(s: number): void {
    this.mt[0] = s >>> 0;
    for (let i = 1; i < N; i++) {
      const prev = this.mt[i - 1] ^ (this.mt[i - 1] >>> 30);
      this.mt[i] = mulUint32(1812433253, prev) + i;
    }
    this.mti = N;
  }

  private initByArray(key: number[]): void {
    this.initGenrand(19650218);
    let i = 1;
    let j = 0;
    for (let k = Math.max(N, key.length); k > 0; k--) {
      const prev = this.mt[i - 1] ^ (this.mt[i - 1] >>> 30);
      this.mt[i] = (this.mt[i] ^ mulUint32(prev, 1664525)) + key[j] + j;
      i++;
      j++;
      if (i >= N) {
        this.mt[0] = this.mt[N - 1];
        i = 1;
      }
      if (j >= key.length) j = 0;
    }
    for (let k = N - 1; k > 0; k--) {
      const prev = this.mt[i - 1] ^ (this.mt[i - 1] >>> 30);
      this.mt[i] = (this.mt[i] ^ mulUint32(prev, 1566083941)) - i;
      i++;
      if (i >= N) {
        this.mt[0] = this.mt[N - 1];
        i = 1;
      }
    }
    this.mt[0] = 0x80000000;
  }

  private genrandUint32(): number {
    const mag01 = [0, MATRIX_A];
    if (this.mti >= N) {
      let kk = 0;
      for (; kk < N - M; kk++) {
        const y = (this.mt[kk] & UPPER_MASK) | (this.mt[kk + 1] & LOWER_MASK);
        this.mt[kk] = this.mt[kk + M] ^ (y >>> 1) ^ mag01[y & 1];
      }
      for (; kk < N - 1; kk++) {
        const y = (this.mt[kk] & UPPER_MASK) | (this.mt[kk + 1] & LOWER_MASK);
        this.mt[kk] = this.mt[kk + (M - N)] ^ (y >>> 1) ^ mag01[y & 1];
      }
      const y = (this.mt[N - 1] & UPPER_MASK) | (this.mt[0] & LOWER_MASK);
      this.mt[N - 1] = this.mt[M - 1] ^ (y >>> 1) ^ mag01[y & 1];
      this.mti = 0;
    }
    let y = this.mt[this.mti++];
    y ^= y >>> 11;
    y ^= (y << 7) & 0x9d2c5680;
    y ^= (y << 15) & 0xefc60000;
    y ^= y >>> 18;
    return y >>> 0;
  }

  random(): number {
    const a = this.genrandUint32() >>> 5;
    const b = this.genrandUint32() >>> 6;
    return (a * 67108864 + b) * (1 / 9007199254740992);
  }
}

export function buildAvatarMatrix(seed: number, rows = 6, colsHalf = 3): number[][] {
  const rng = new MT19937(seed);
  const halfMatrix: number[][] = Array.from({ length: rows }, () => new Array(colsHalf).fill(0));
  const centerRow = (rows - 1) / 2;
  const deltas: Array<[number, number]> = [[-1, 0], [0, -1], [-1, -1], [-1, 1]];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < colsHalf; col++) {
      let neighbors = 0;
      for (const [dRow, dCol] of deltas) {
        const nRow = row + dRow;
        const nCol = col + dCol;
        if (nRow >= 0 && nRow < rows && nCol >= 0 && nCol < colsHalf) {
          neighbors += halfMatrix[nRow][nCol];
        }
      }
      const centerBias = 1 - Math.abs(row - centerRow) / Math.max(centerRow, 1);
      let probability = 0.28 + neighbors * 0.18 + centerBias * 0.2;
      probability = Math.max(0.08, Math.min(0.92, probability));
      halfMatrix[row][col] = rng.random() < probability ? 1 : 0;
    }
  }

  const allZero = halfMatrix.every((r) => r.every((c) => c === 0));
  const allOne = halfMatrix.every((r) => r.every((c) => c === 1));
  if (allZero) {
    halfMatrix[Math.floor(rows / 2)][Math.floor(colsHalf / 2)] = 1;
  } else if (allOne) {
    halfMatrix[0][0] = 0;
  }

  return halfMatrix.map((row) => [...row, ...[...row].reverse()]);
}
